import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Avatar,
  IconButton,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Paper,
  Typography,
  Divider,
} from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import EditIcon from "@mui/icons-material/Edit";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CloseIcon from "@mui/icons-material/Close";
import ImageCropperDialog from "../common/ImageCropperDialog";
import { toastError } from "../../../utils2024/utils";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import {
  getHeader,
  getMultipartHeader,
  getToken,
} from "../../../utility/utility";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINT } from "../../../constants/constants";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

const AddTeamMembers = () => {
  const navigate = useNavigate(false);
  const { id } = useParams();
  const [showCropper, setShowCropper] = useState(false);
  const [croppedImage, setCroppedImage] = useState(null); // For display
  const [croppedImageBlob, setCroppedImageBlob] = useState(null); // For upload
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validId, setValidId] = useState(null);
  const [picUrl, setPicUrl] = useState("");
  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    role: "",
    designation: "",
  });

  // Handle form field changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle phone number change
  const handlePhoneNumberChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      phone: value,
    }));
  };
  const handleCroppedImage = (blob) => {
    setCroppedImageBlob(blob);
    const reader = new FileReader();
    reader.onloadend = () => {
      setCroppedImage(reader.result);
    };
    reader.readAsDataURL(blob);
    setShowCropper(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API_ENDPOINT}/user/get-team-member-by-id?id=${id}`,
          getHeader()
        );
        setFormData((prevValues) => ({
          ...prevValues,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          email: data.email,
          role: data.role,
          designation: data.designation || "",
        }));
        setPicUrl(data.profile_image);
      } catch (error) {
        toastError(error);
        setValidId(false);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  if (validId !== null && validId === false) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Invalid request ID!
        </Alert>
      </Container>
    );
  }

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Create FormData object
    const submitData = new FormData();
    submitData.append("firstName", formData.firstName);
    submitData.append("lastName", formData.lastName);
    submitData.append("phone", formData.phone);
    submitData.append("email", formData.email);
    submitData.append("role", formData.role);
    submitData.append("designation", formData.designation);

    if (!!croppedImageBlob) {
      submitData.append("image", croppedImageBlob, "profile_image.png");
    }
    try {
      setSubmitting(true);
      const { data } = await axios.post(
        id
          ? `${API_ENDPOINT}/user/edit-team-member?id=${id}`
          : `${API_ENDPOINT}/user/add-team-member`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      toast.success(data.message);
      if (!id) {
        setFormData({
          firstName: "",
          lastName: "",
          phone: "",
          email: "",
          role: "",
          designation: "",
        });
        setCroppedImage(null);
        setCroppedImageBlob(null);
      }
      setTimeout(() => {
        navigate("/exhibitor/team-members", { replace: true });
      }, 300);
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", mt: 2, mb: 4, px: 2 }}>
      <Button
        onClick={() => navigate(-1)}
        variant="text"
        size="small"
        startIcon={<ArrowBackIosNewIcon fontSize="small" />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          color: "text.secondary",
          mb: 2,
          "&:hover": { backgroundColor: "#f1f5f9" },
        }}
      >
        Back
      </Button>

      <Box sx={{ maxWidth: 700, margin: "0 auto" }}>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ borderRadius: 3, overflow: "hidden" }}
        >
          {/* Header Section */}
          <Box
            sx={{
              bgcolor: "#f8fafc",
              p: 3,
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                bgcolor: "primary.main",
                color: "white",
                borderRadius: "12px",
                width: 48,
                height: 48,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              <PersonAddAlt1Icon fontSize="medium" />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="700" color="#1e293b">
                {id ? "Edit Team Member" : "Add Team Member"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {id
                  ? "Update the details for this team member."
                  : "Fill out the details to invite a new member to your team."}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Profile Picture */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 4,
              }}
            >
              <Box sx={{ position: "relative" }}>
                <Avatar
                  src={croppedImage ? croppedImage : picUrl}
                  alt="Profile Picture"
                  sx={{
                    width: 120,
                    height: 120,
                    backgroundColor: "#f1f5f9",
                    border: "4px solid white",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                    color: "#94a3b8",
                  }}
                >
                  {!croppedImage && !picUrl && (
                    <PhotoCameraIcon sx={{ fontSize: 40 }} />
                  )}
                </Avatar>
                <IconButton
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    backgroundColor: "primary.main",
                    color: "white",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    "&:hover": { backgroundColor: "primary.dark" },
                  }}
                  size="small"
                  onClick={() => setShowCropper(true)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1.5, fontWeight: 500 }}
              >
                Upload Profile Picture
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Form Fields */}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="600"
                    color="#475569"
                    mb={1}
                  >
                    First Name *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="firstName"
                    placeholder="e.g. John"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#fcfcfc",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="600"
                    color="#475569"
                    mb={1}
                  >
                    Last Name *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="lastName"
                    placeholder="e.g. Doe"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#fcfcfc",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="600"
                    color="#475569"
                    mb={1}
                  >
                    Email Address *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="email"
                    type="email"
                    placeholder="e.g. john@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#fcfcfc",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="600"
                    color="#475569"
                    mb={1}
                  >
                    Phone Number
                  </Typography>
                  <MuiTelInput
                    fullWidth
                    defaultCountry="IN"
                    forceCallingCode
                    value={formData.phone}
                    onChange={handlePhoneNumberChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#fcfcfc",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="600"
                    color="#475569"
                    mb={1}
                  >
                    Role *
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#fcfcfc",
                      },
                    }}
                  >
                    <Select
                      name="role"
                      displayEmpty
                      value={formData.role}
                      onChange={(event) =>
                        handleInputChange({
                          target: { name: "role", value: event.target.value },
                        })
                      }
                    >
                      <MenuItem value="" disabled>
                        Select a role
                      </MenuItem>
                      <MenuItem value="Admin">Admin</MenuItem>
                      <MenuItem value="User">User</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="600"
                    color="#475569"
                    mb={1}
                  >
                    Designation *
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#fcfcfc",
                      },
                    }}
                  >
                    <Select
                      name="designation"
                      displayEmpty
                      value={formData.designation}
                      onChange={(event) =>
                        handleInputChange({
                          target: {
                            name: "designation",
                            value: event.target.value,
                          },
                        })
                      }
                    >
                      <MenuItem value="" disabled>
                        Select a designation
                      </MenuItem>
                      {[
                        "Advisor",
                        "Application Engineer",
                        "Assistant Director",
                        "Assistant Engineer",
                        "Assistant Executive Manager",
                        "Assistant General Manager",
                        "Automation Manager",
                        "Business Development Manager",
                        "Chairman",
                        "Chief Engineer",
                        "Chief Executive Officer",
                        "Chief General Manager",
                        "Chief Operating Officer",
                        "Chief Technical Officer",
                        "CNC Engineer",
                        "CNC Machine Operator",
                        "CNC Manager",
                        "CNC Programmer",
                        "Consultant",
                        "Contractor",
                        "Country Head",
                        "Deputy Director",
                        "Deputy General Manager",
                        "Deputy Manager",
                        "Design Engineer",
                        "Design Manager",
                        "Director",
                        "Engineer",
                        "Executive",
                        "Executive Director",
                        "Executive Manager",
                        "General Manager",
                        "Head-Engineering",
                        "Head-Maintenance",
                        "Head-Manufacturing",
                        "Head-Operations",
                        "Head-Production",
                        "Head-Purchase",
                        "Head-Quality",
                        "Head-R&D",
                        "Head-Sales",
                        "Industrial Engineer",
                        "Junior Engineer",
                        "Junior Manager",
                        "Junior Technician",
                        "Machine Operator",
                        "Maintenance Engineer",
                        "Maintenance Manager",
                        "Manager",
                        "Managing Director",
                        "Manufacturing Engineer",
                        "Manufacturing Manager",
                        "Mechanical Engineer",
                        "Operations Manager",
                        "Owner",
                        "Partner",
                        "Plant Head",
                        "Plant In-Charge",
                        "Plant Manager",
                        "President",
                        "Process Engineer",
                        "Procurement Manager",
                        "Production Engineer",
                        "Production Manager",
                        "Programmer",
                        "Project Engineer",
                        "Project Manager",
                        "Proprietor",
                        "Purchase Manager",
                        "Quality Engineer",
                        "Quality Inspector",
                        "Quality Manager",
                        "R&D Engineer",
                        "R&D Manager",
                        "Sales Engineer",
                        "Sales Manager",
                        "Senior Director",
                        "Senior General Manager",
                        "Senior Vice President",
                        "Service Engineer",
                        "Service Manager",
                        "Supply Chain Manager",
                        "Technician",
                        "Tooling Engineer",
                        "Tooling Manager",
                        "Trainee",
                        "Vice President",
                      ].map((designation) => (
                        <MenuItem key={designation} value={designation}>
                          {designation}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 4,
                  pt: 2,
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    mr: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    color: "text.secondary",
                    borderColor: "#cbd5e1",
                  }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  loading={submitting}
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                  }}
                >
                  {id ? "Save Changes" : "Add Member"}
                </LoadingButton>
              </Box>
            </form>
          </Box>
        </Paper>
      </Box>
      <ImageCropperDialog
        title="Crop Profile Image"
        open={showCropper}
        onClose={() => setShowCropper(false)}
        aspectRatio={1}
        onCropped={handleCroppedImage}
      />
    </Box>
  );
};

export default AddTeamMembers;
